import { formatKm } from '@shared/format';
import type { Leg, Path } from '@shared/model/dataset';
import { Figures } from './Figures';
import styles from './Steps.module.css';

interface Props {
    path: Path;
}

function hasBadges(leg: Leg): boolean {
    return leg.closed || !!leg.info;
}

/** Step by step detail of an itinerary: one row per leg with its own figures. */
export function Steps({ path }: Props) {
    // Distance walked before each leg.
    const distanceBefore: number[] = [];
    path.legs.reduce((sum, leg) => {
        distanceBefore.push(sum);
        return sum + leg.distance;
    }, 0);
    return (
        <div className={styles.steps}>
            <div className={styles.dots} aria-hidden="true">
                <div className={styles.firstPadding} />
                {path.legs.map((leg) => (
                    <div key={leg.code} className={styles.dotAndLine}>
                        <div className={styles.circle} />
                        <div
                            className={`${styles.line} ${hasBadges(leg) ? styles.withInfo : ''}`}
                            style={{ borderColor: leg.color }}
                        />
                    </div>
                ))}
                <div className={styles.circle} />
            </div>
            <div className={styles.rows}>
                {path.legs.map((leg, index) => {
                    const distanceSoFar = distanceBefore[index] ?? 0;
                    const withInfo = hasBadges(leg);
                    return (
                        <div
                            key={leg.code}
                            className={`${styles.step} ${withInfo ? styles.withInfo : ''}`}
                            data-testid="step"
                        >
                            <div className={styles.stepStart}>
                                {index === 0 ? <b>{leg.start.name}</b> : leg.start.name}
                            </div>
                            <div className={styles.stepDistance}>
                                {distanceSoFar > 0 ? formatKm(distanceSoFar) : ''}
                            </div>
                            {withInfo && (
                                <div className={styles.info}>
                                    {leg.closed && (
                                        <span className="badge badge--closed" title="Chemin fermé par l'ONF">
                                            ⛔ Fermé
                                        </span>
                                    )}
                                    {leg.info && <span className="badge badge--info">ℹ️ {leg.info}</span>}
                                </div>
                            )}
                            <div className={styles.details}>
                                <Figures
                                    distance={leg.distance}
                                    duration={leg.duration}
                                    up={leg.up}
                                    down={leg.down}
                                    size="sm"
                                />
                            </div>
                        </div>
                    );
                })}
                <div className={styles.end}>
                    <b>{path.end.name}</b>
                    <b>{formatKm(path.distance)}</b>
                </div>
            </div>
        </div>
    );
}
